import NiceModal from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import AutoComplete from 'components/field/AutoComplete';
import Button from 'components/field/Button';
import EnumSelect from 'components/field/EnumSelect';
import IntegerInput from 'components/field/IntegerInput';
import StringInput from 'components/field/StringInput';
import TextArea from 'components/field/TextArea';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Alert from 'components/global/Alert';
import ContextMenu from 'components/global/ContextMenu';
import MinimizeButton from 'components/global/MinimizeButton';
import Modal from 'components/global/Modal';
import ModuleToolTipContainer from 'components/global/ModuleToolTipContainer';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { updateDynamicListData } from 'components/list/dynamicListSlice';

import useAccessControl from 'hooks/useAccessControl';
import useAlert from 'hooks/useAlert';
import useAppTracking from 'hooks/useAppTracking';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useLocale from 'hooks/useLocale';
import useModalRouter from 'hooks/useModalRouter';
import { usePrompt } from 'hooks/usePrompt';
import useRestriction, { SYSTEM_LIMITS } from 'hooks/useRestriction';

import { AUTOCOMPLETE, ENUMS, SMS } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { DYNAMICLISTS } from 'utils/constants';
import PATHS, { mergePaths } from 'utils/paths';

import { faEye } from '@fortawesome/pro-solid-svg-icons';

import BrandForm from '../Brands/BrandForm';

const CONTEXT_MENU_ITEMS = {
	clientName: { label: 'clientName', value: 'clientName' },
	link: { label: 'link', value: 'link' },
	orderNumber: { label: 'orderNumber', value: 'orderNumber' },
	trackingNumber: { label: 'trackingNumber', value: 'trackingNumber' },
	deliveryCompany: { label: 'deliveryCompany', value: 'deliveryCompany' },
	customerName: { label: 'customerName', value: 'customerName' },
	OTP: { label: 'OTP', value: 'OTP' },
	branchName: { label: 'branchName', value: 'branchName' },
	branchLocation: { label: 'branchLocation', value: 'branchLocation' },
	ref1: { label: 'ref1', value: 'ref1' }
};

const SMS_MENU_ITEMS = {
	trackingSms: [
		CONTEXT_MENU_ITEMS.clientName,
		CONTEXT_MENU_ITEMS.link,
		CONTEXT_MENU_ITEMS.orderNumber,
		CONTEXT_MENU_ITEMS.trackingNumber,
		CONTEXT_MENU_ITEMS.deliveryCompany,
		CONTEXT_MENU_ITEMS.customerName,
		CONTEXT_MENU_ITEMS.ref1
	],
	feedbackSms: [
		CONTEXT_MENU_ITEMS.clientName,
		CONTEXT_MENU_ITEMS.link,
		CONTEXT_MENU_ITEMS.orderNumber,
		CONTEXT_MENU_ITEMS.trackingNumber,
		CONTEXT_MENU_ITEMS.deliveryCompany,
		CONTEXT_MENU_ITEMS.customerName,
		CONTEXT_MENU_ITEMS.ref1
	],
	undeliveredAttemptSms: [
		CONTEXT_MENU_ITEMS.clientName,
		CONTEXT_MENU_ITEMS.link,
		CONTEXT_MENU_ITEMS.orderNumber,
		CONTEXT_MENU_ITEMS.trackingNumber,
		CONTEXT_MENU_ITEMS.deliveryCompany,
		CONTEXT_MENU_ITEMS.customerName,
		CONTEXT_MENU_ITEMS.ref1
	],
	returnRequestSMS: [
		CONTEXT_MENU_ITEMS.clientName,
		CONTEXT_MENU_ITEMS.link,
		CONTEXT_MENU_ITEMS.orderNumber,
		CONTEXT_MENU_ITEMS.customerName,
		CONTEXT_MENU_ITEMS.ref1
	],
	orderConfirmationSms: [
		CONTEXT_MENU_ITEMS.clientName,
		CONTEXT_MENU_ITEMS.link,
		CONTEXT_MENU_ITEMS.orderNumber,
		CONTEXT_MENU_ITEMS.customerName,
		CONTEXT_MENU_ITEMS.ref1
	],
	addressVerificationSms: [
		CONTEXT_MENU_ITEMS.clientName,
		CONTEXT_MENU_ITEMS.link,
		CONTEXT_MENU_ITEMS.orderNumber,
		CONTEXT_MENU_ITEMS.customerName,
		CONTEXT_MENU_ITEMS.ref1
	],
	manualSms: [
		CONTEXT_MENU_ITEMS.clientName,
		CONTEXT_MENU_ITEMS.orderNumber,
		CONTEXT_MENU_ITEMS.trackingNumber,
		CONTEXT_MENU_ITEMS.deliveryCompany,
		CONTEXT_MENU_ITEMS.customerName,
		CONTEXT_MENU_ITEMS.OTP,
		CONTEXT_MENU_ITEMS.branchName,
		CONTEXT_MENU_ITEMS.branchLocation,
		CONTEXT_MENU_ITEMS.ref1
	],
	deliveryOTPSms: [
		CONTEXT_MENU_ITEMS.clientName,
		CONTEXT_MENU_ITEMS.orderNumber,
		CONTEXT_MENU_ITEMS.trackingNumber,
		CONTEXT_MENU_ITEMS.deliveryCompany,
		CONTEXT_MENU_ITEMS.customerName,
		CONTEXT_MENU_ITEMS.OTP,
		CONTEXT_MENU_ITEMS.ref1
	],
	clickCollectSms: [
		CONTEXT_MENU_ITEMS.clientName,
		CONTEXT_MENU_ITEMS.orderNumber,
		CONTEXT_MENU_ITEMS.customerName,
		CONTEXT_MENU_ITEMS.OTP,
		CONTEXT_MENU_ITEMS.branchName,
		CONTEXT_MENU_ITEMS.branchLocation,
		CONTEXT_MENU_ITEMS.ref1
	],
	deliverySlotSms: [
		CONTEXT_MENU_ITEMS.link,
		CONTEXT_MENU_ITEMS.orderNumber,
		CONTEXT_MENU_ITEMS.customerName,
		CONTEXT_MENU_ITEMS.deliveryCompany,
		CONTEXT_MENU_ITEMS.ref1
	],
	returnRequestApprovalSms: [
		CONTEXT_MENU_ITEMS.clientName,
		CONTEXT_MENU_ITEMS.orderNumber,
		CONTEXT_MENU_ITEMS.customerName,
		CONTEXT_MENU_ITEMS.branchName,
		CONTEXT_MENU_ITEMS.branchLocation,
		CONTEXT_MENU_ITEMS.ref1
	],
	returnRequestRejectionSms: [
		CONTEXT_MENU_ITEMS.clientName,
		CONTEXT_MENU_ITEMS.orderNumber,
		CONTEXT_MENU_ITEMS.customerName,
		CONTEXT_MENU_ITEMS.branchName,
		CONTEXT_MENU_ITEMS.branchLocation,
		CONTEXT_MENU_ITEMS.ref1
	],
	rescheduleSms: [
		CONTEXT_MENU_ITEMS.clientName,
		CONTEXT_MENU_ITEMS.link,
		CONTEXT_MENU_ITEMS.orderNumber,
		CONTEXT_MENU_ITEMS.trackingNumber,
		CONTEXT_MENU_ITEMS.deliveryCompany,
		CONTEXT_MENU_ITEMS.customerName,
		CONTEXT_MENU_ITEMS.OTP,
		CONTEXT_MENU_ITEMS.branchName,
		CONTEXT_MENU_ITEMS.branchLocation,
		CONTEXT_MENU_ITEMS.ref1
	]
};

const SMSSettingForm = NiceModal.create(({ id, mode, modalName }) => {
	// Hooks and states declaration
	const isAuthorized = useAccessControl();
	const dispatch = useDispatch();
	const { LanguageCode } = useLocale();
	const axios = useAxiosPrivate();
	const { showError, showSuccess } = useAlert();
	const navigate = useNavigate();
	const isRestricted = useRestriction();
	const { closeModal } = useModalRouter();
	const { track } = useAppTracking();
	const largeDefaultColSpan = {
		md: 12
	};

	const defaultColSpan = {
		md: 6
	};
	const [isMinimizing, setIsMinimizing] = useState(false);

	const [isLoading, setIsLoading] = useState(false);
	const [textAreaRef, setTextAreaRef] = useState(null);
	const [isContextMenuOpen, setIsContextMenuOpen] = useState(false);
	const [anchorPoint, setAnchorPoint] = useState({ x: 0, y: 0 });
	const contextMenuList = useRef(null);

	// Validation schema definition
	const schema = useMemo(
		() =>
			yup
				.object()
				.shape({
					smsType: yup.string().nullable().required(),
					definition: yup
						.string()
						.nullable()
						.when('smsType', {
							is: 'manualSms',
							then: (schema) => schema.required()
						}),
					smsConfigID: yup.string().nullable().required(),
					language: yup
						.string()
						.nullable()
						.when('smsType', {
							is: 'manualSms',
							then: (schema) => schema,
							otherwise: (schema) => schema.required()
						}),
					content: yup.string().nullable().required(),
					status: yup.string().nullable().required(),
					brandID: yup.string().nullable(),
					waitingAddressConfirmationTime: yup.number().nullable().min(0)
				})
				.required(),
		[]
	);

	// Form setup using react-hook-form
	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			smsType: null,
			definition: null,
			smsConfigID: null,
			brandID: null,
			language: LanguageCode ?? null,
			content: null,
			status: 'active',
			waitingAddressConfirmationTime: 30
		}
	});

	const {
		handleSubmit,
		setValue,
		watch,
		reset,
		formState: { isDirty, isSubmitting, isSubmitSuccessful, errors }
	} = formMethods;

	usePrompt(isDirty && !isSubmitting && !isSubmitSuccessful && !isMinimizing);

	const smsType = watch('smsType');
	useEffect(() => {
		if (mode === 'edit') {
			getSMSSettings();
		}
	}, [mode]);

	const smsTypes = useMemo(() => {
		const types = ['trackingSms', 'feedbackSms', 'undeliveredAttemptSms']; // Starter package SMS types

		if (isAuthorized('ScalePackage') || isAuthorized('EnterprisePackage')) {
			types.push('returnRequestSMS');
		}

		if (isAuthorized('OMSModule')) {
			types.push('orderConfirmationSms');
		}

		if (isAuthorized('OTOFlexModule')) {
			types.push('deliveryOTPSms');
		}

		if (isAuthorized('ClickAndCollectModule')) {
			types.push('clickCollectSms');
		}

		if (isAuthorized('EnterprisePackage')) {
			types.push('addressVerificationSms');
			types.push('rescheduleSms');
			types.push('manualSms');
			types.push('returnRequestApprovalSms');
			types.push('returnRequestRejectionSms');
			types.push('deliverySlotSms');
		}
		return types;
	}, []);

	const handleCloseModal = () => {
		closeModal();
	};

	const getSMSSettings = async () => {
		setIsLoading(true);
		try {
			const response = await axios.post(SMS.GET_SMS_SETTINGS, {
				ID: id
			});

			if (response.status === 200) {
				reset({
					...response.data
				});
			}
		} catch (error) {
			AppLogger.error(error);
		} finally {
			setIsLoading(false);
		}
	};

	const handleInsertText = useCallback(
		(input) => {
			const curPos = textAreaRef.selectionStart;

			const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
				window.HTMLTextAreaElement.prototype,
				'value'
			).set;

			const value = textAreaRef.value;

			nativeInputValueSetter.call(textAreaRef, value.slice(0, curPos) + input + value.slice(curPos));

			const inputEvent = new Event('input', { bubbles: true });
			textAreaRef.dispatchEvent(inputEvent);
		},
		[textAreaRef]
	);

	useEffect(() => {
		contextMenuList.current = SMS_MENU_ITEMS[smsType]?.map((menuItem) => ({
			label: menuItem.label,
			action: () => handleInsertText(`{{${menuItem.value}}}`)
		}));
	}, [SMS_MENU_ITEMS, smsType]);

	const handleOnContextMenu = useCallback(
		(event) => {
			if (!contextMenuList.current || contextMenuList.current?.length === 0) {
				return;
			}
			event.preventDefault();
			event.stopPropagation();
			if (event.pageX) {
				setAnchorPoint({ x: event.pageX, y: event.pageY });
			} else {
				const curPos = textAreaRef?.selectionStart;
				const { left, top } = textAreaRef.getBoundingClientRect();
				const { offsetWidth, offsetHeight } = textAreaRef;
				const { offsetLeft, offsetTop } = textAreaRef;
				const { scrollLeft, scrollTop } = textAreaRef;
				const { clientWidth, clientHeight } = textAreaRef;

				const pagex = Math.min(
					left + offsetLeft + scrollLeft + (curPos * offsetWidth) / clientWidth,
					window?.innerWidth - 20
				);
				const pagey = Math.min(
					top + offsetTop + scrollTop + offsetHeight / clientHeight,
					window?.innerHeight - 20
				);

				setAnchorPoint({ x: pagex, y: pagey });
			}

			setIsContextMenuOpen(true);
		},
		[textAreaRef]
	);

	useEffect(() => {
		textAreaRef?.addEventListener?.('keydown', (e) => {
			if (e.key === '/') {
				handleOnContextMenu(e);
			}
		});
		return () => {
			textAreaRef?.removeEventListener?.('keydown', handleOnContextMenu);
		};
	}, [textAreaRef]);

	const onSubmit = async (data) => {
		setIsLoading(true);

		const URL = mode === 'edit' ? SMS.UPDATE_SMS_SETTINGS : SMS.ADD_SMS_SETTINGS;
		try {
			const response = await axios.post(URL, {
				ID: id || undefined,
				...data,
				smsConfigID: data.smsConfigID,
				brandID: smsType === 'manualSms' ? null : data.brandID,
				language: smsType === 'manualSms' ? null : data.language
			});

			if (response.status === 200) {
				dispatch(updateDynamicListData(DYNAMICLISTS.SMS_SETTINGS));
				handleCloseModal();
				showSuccess();
			} else {
				AppLogger.error(response);
			}
			reset();
		} catch (error) {
			if (error.response?.data.code === 'error.B010') {
				showError({
					header: 'errorMsg.sameTemplateAlreadyExist',
					message: 'errorMsg.youCanNotAddSameSMSTypeandBrandandLanguage'
				});
			} else if (error.response?.data.code === 'error.B016') {
				showError({
					header: 'errorMsg.sameTemplateAlreadyExist',
					message: 'errorMsg.youCanNotAddSameManualSmsName'
				});
			} else if (error.response?.data.code === 'error.B015') {
				track('moreThan1SMS');
				showError({
					header: 'errorMsg.freePackageUserOnlyOneSMSHeader',
					message: 'errorMsg.freePackageUserOnlyOneSMSMessage'
				});
			} else {
				AppLogger.error(error);
				showError();
			}
		}

		setIsLoading(false);
	};

	return (
		<Modal
			fullwidth
			maxWidth="medium"
			name={modalName}
			className="modal-smsSetting"
			minimizeButton={(show, close) => (
				<MinimizeButton
					onShow={() => {
						show();
						setIsMinimizing(false);
					}}
					onClose={close}
					label={mode === 'edit' ? 'editSMSSettings' : 'addSMSSettings'}
				/>
			)}
			onMinimize={() => {
				setIsMinimizing(true);
			}}>
			<ModalTitle>
				<Typography variant="p1">{mode === 'edit' ? 'editSMSSettings' : 'addSMSSettings'}</Typography>
			</ModalTitle>
			<ModalContent>
				<Form formMethods={formMethods}>
					<FormItem label="smsType" colSpan={defaultColSpan} isLoading={isLoading} required>
						<EnumSelect
							searchable={false}
							disabled={mode === 'edit'}
							data={smsTypes}
							value={smsType}
							name={`smsType`}
							setValue={(value) => {
								setValue(`smsType`, value, { shouldValidate: true, shouldDirty: true });
							}}
						/>
					</FormItem>

					{smsType === 'manualSms' && (
						<FormItem
							label="manualSmsName"
							colSpan={defaultColSpan}
							isLoading={isLoading}
							required
							toolTip="manualSmsName.tooltip">
							<StringInput name="definition" />
						</FormItem>
					)}

					<FormItem label="smsProviders" colSpan={defaultColSpan} isLoading={isLoading} required>
						<AutoComplete
							autoCompleteName={AUTOCOMPLETE.SMS_CONFIG}
							columns={[{ accessor: 'Name', width: 200 }]}
							selectionLabelAccessor={['Name']}
							value={watch('smsConfigID')}
							name={`smsConfigID`}
							firstItem={{
								action: () => navigate(mergePaths(PATHS.SMS_PROVIDERS, PATHS.ALL_PROVIDERS)),
								label: <Typography color="var(--blue)">addANewSMSProvider</Typography>
							}}
							setValue={(value) => {
								setValue('smsConfigID', value?.ID ?? null, {
									shouldValidate: true,
									shouldDirty: true
								});
							}}
						/>
					</FormItem>

					{smsType !== 'manualSms' && (
						<FormItem
							label="brand"
							colSpan={defaultColSpan}
							isLoading={isLoading}
							roleName="BrandingModule">
							<AutoComplete
								autoCompleteName={AUTOCOMPLETE.BRANDS}
								columns={[{ accessor: 'Name', width: 200 }]}
								selectionLabelAccessor={['Name']}
								value={watch('brandID')}
								setValue={(value) => {
									setValue('brandID', value?.ID ?? null, { shouldValidate: true, shouldDirty: true });
								}}
								firstItem={
									isAuthorized('/autocomplete/brand/addANewBrand')
										? {
												action: () =>
													NiceModal.show(BrandForm, {
														setBrand: (value) => {
															setValue(`brandID`, value, {
																shouldValidate: true,
																shouldDirty: true
															});
														}
													}),
												label: <Typography color="var(--blue)">addANewBrand</Typography>,
												disabled: (dataSize) => isRestricted(SYSTEM_LIMITS.BRAND, dataSize),
												disabledTooltip: (
													<div style={{ display: 'block' }}>
														<ModuleToolTipContainer
															infoImage="lottie_multiBrand"
															title="multiBrandModule"
															description="multiBrandModuleSubtitle"
														/>

														<Button
															style={{ marginLeft: 'Auto', marginRight: 'Auto' }}
															className="mt-3"
															label="showPlans"
															icon={faEye}
															variant="blue"
															onMouseDown={() => {
																track('moreThan3Brands');
																navigate(PATHS.OTO_MODULES);
															}}
														/>
													</div>
												)
											}
										: undefined
								}
							/>
						</FormItem>
					)}

					{smsType !== 'manualSms' && (
						<FormItem label="language" colSpan={defaultColSpan} isLoading={isLoading} required>
							<EnumSelect
								searchable={false}
								valueRemovable={false}
								enumName={ENUMS.LANGUAGE_CODES}
								value={watch(`language`)}
								setValue={(value) => {
									setValue(`language`, value, { shouldValidate: true, shouldDirty: true });
								}}
							/>
						</FormItem>
					)}

					<FormItem label="status" colSpan={defaultColSpan} isLoading={isLoading} required>
						<EnumSelect
							searchable={false}
							enumName={ENUMS.ACTIVE_INACTIVE}
							name={`status`}
							valueRemovable={false}
							value={watch(`status`)}
							setValue={(value) => {
								setValue(`status`, value === 'deliveryOTPSms' ? 'active' : value, {
									shouldValidate: true,
									shouldDirty: true
								});
							}}
						/>
					</FormItem>
					<FormItem
						roleName="EnterprisePackage"
						toolTip="tooltip.cutOffTimeMins"
						label="cutOffTimeMins"
						colSpan={defaultColSpan}
						isLoading={isLoading}>
						<IntegerInput name="waitingAddressConfirmationTime" showArrows={false} />
					</FormItem>

					<FormItem colSpan={defaultColSpan} style={{ display: 'none' }}>
						<StringInput name="templateName" viewMode />
					</FormItem>

					<FormItem
						label="smsContent"
						toolTip="smsContent.description"
						colSpan={largeDefaultColSpan}
						isLoading={isLoading}
						required>
						<TextArea
							ref={setTextAreaRef}
							name="content"
							errorMessage={errors?.content?.message}
							placeholder="smsContent.placeholder"
							onContextMenu={handleOnContextMenu}
							maxlength={1600}
						/>

						{contextMenuList.current && isContextMenuOpen && (
							<ContextMenu
								menuItems={contextMenuList.current}
								anchorPoint={anchorPoint}
								onClose={() => {
									setIsContextMenuOpen(false);
								}}
								isContextMenuOpen={isContextMenuOpen}
								setIsContextMenuOpen={setIsContextMenuOpen}
							/>
						)}
					</FormItem>
					{!isLoading && !['manualSms', 'deliveryOTPSms', 'clickCollectSms'].includes(smsType) && (
						<FormItem colSpan={largeDefaultColSpan}>
							<Alert showIcon content="smsFormSettingsInfoText" />
						</FormItem>
					)}
				</Form>
			</ModalContent>
			<ModalActions>
				<Button label="cancel" variant="outline" onClick={handleCloseModal} />
				<Button
					label={mode === 'edit' ? 'save' : 'button.add'}
					loading={isLoading}
					disabled={!isDirty && isAuthorized('SMSModule') !== false}
					disabledTooltip={!isDirty && 'formInfo.noChanges'}
					onClick={handleSubmit(onSubmit, (errors) => {
						AppLogger.log(errors);
					})}
					iconPos="right"
				/>
			</ModalActions>
		</Modal>
	);
});
export default SMSSettingForm;
